import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { ClockProvider } from "@/providers/ClockContext";
import { clearToken, getToken, setToken } from "@/lib/auth-token";
import { ApiError, apiFetch } from "@/lib/api-client";
import type { CurrentUser } from "@/types/auth";

// staleTime per the aggressive-caching directive: dashboard numbers are minutes-old
// aggregates already (analytics.py's own 30-day windows), so treating them as fresh
// for 5 minutes trades a small staleness window for not re-fetching on every tab focus.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status === 401) return false;
        // A 429 is the server saying "wait" -- Meta's hourly WhatsApp limit, for one. Retrying
        // at once spent two more calls against it per failure and pushed the reset back.
        if (error instanceof ApiError && error.status === 429) return false;
        return failureCount < 2;
      },
    },
  },
});

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  /** Stores the JWT and resolves the signed-in identity. Throws if the token is
   *  rejected, so the caller can surface the failure instead of half-signing-in. */
  signIn: (accessToken: string, remember?: boolean) => Promise<void>;
  /** Re-read who is signed in, after something the server holds about them changed --
   *  a temporary password replaced, for one. Throws if the read fails. */
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AppProviders>");
  return ctx;
}

function AuthBoundary({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function loadUser() {
      const token = getToken();
      if (!token) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      try {
        const me = await apiFetch<CurrentUser>("/api/auth/me");
        if (!cancelled) setUser(me);
      } catch (error) {
        // get_current_user (Backend/app/shared/core/deps.py) raises 401 for every
        // case where a token WAS sent and rejected (invalid, expired, user not
        // found) -- 401 is the only reliable "this token is bad" signal. A 403 here
        // comes from FastAPI's HTTPBearer itself, before get_current_user even runs,
        // and means NO Authorization header was sent at all -- that's evidence of a
        // client-side bug or a request that raced ahead of the token being set, not
        // evidence the stored token is invalid. Clearing it on 403 previously turned
        // one such request into a permanent, silent logout with no way to tell why
        // "Not authenticated" kept showing up afterward.
        if (error instanceof ApiError && error.status === 401) {
          clearToken();
        }
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void loadUser();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (accessToken: string, remember = true) => {
    // `remember` decides WHICH store, not whether to store — see lib/auth-token.ts.
    setToken(accessToken, remember);
    try {
      const me = await apiFetch<CurrentUser>("/api/auth/me");
      setUser(me);
    } catch (error) {
      // Never leave a token behind that the server will not honour -- that is
      // exactly the state that produced a signed-in-looking shell with no data.
      clearToken();
      setUser(null);
      throw error;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    setUser(await apiFetch<CurrentUser>("/api/auth/me"));
  }, []);

  // Full teardown, in this order: drop the credential, drop every cached response
  // fetched under it, then leave. queryClient.clear() matters -- without it the next
  // sign-in can paint the PREVIOUS user's cached rows before its own fetches land.
  //
  // A hard navigation (not react-router) is deliberate: it guarantees no in-memory
  // state survives, and it targets /login rather than "/" -- "/" redirects to
  // /dashboard, which the guard bounces straight back, which was the redirect loop.
  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    queryClient.clear();
    window.location.assign("/login");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, signIn, refreshUser, logout }),
    [user, isLoading, signIn, refreshUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ClockProvider>
          <AuthBoundary>{children}</AuthBoundary>
        </ClockProvider>
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
